import React, { useState, useEffect, useRef } from 'react'
import moment from 'moment'
import { useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import useFetch from 'hooks/useFetch';
import { BASE_URL } from 'utils/apiFunctions';
import useInterval from 'use-interval';
import ObsGame from 'components/Obs/ObsGame';
import sortGameEvents from 'utils/sortGameEvents';
import usePageTitle from 'hooks/usePageTitle';

const UPDATE_CHECK_INTERVAL = 1000;
const FORCE_RELOAD_EVERY_N_UPDATES = 15;
const GAME_STREAK_MAX_LENGTH = 15;

const ObsGameWrapper = () => {
  usePageTitle('OBS - Game');

  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const [lastUpdatedAtState, setLastUpdatedAtState] = useState(null);

  const { data: updatedAtData, reload: reloadUpdatedAt, reloadCounter } = useFetch(BASE_URL + '/obs/lastUpdatedAt?id=' + id);
  const { data: gameData, reload: reloadGameData, isError: reloadError } = useFetch(BASE_URL + '/obs/game?id=' + id);

  useInterval(() => {
    reloadUpdatedAt();
  }, UPDATE_CHECK_INTERVAL);

  // retry loading of data if the last attempt failed
  useEffect(() => {
    if (reloadError) {
      console.log('reload error - forcing reload...');
      setTimeout(() => {
        reloadGameData();
      }, 1000);
    }
  }, [reloadError]);

  // force reload every N updates just to make data refresh absolutely sure
  useEffect(() => {
    if (reloadCounter % FORCE_RELOAD_EVERY_N_UPDATES === 0) {
      console.log('force reloading game data...');
      reloadGameData();
    }
  }, [reloadCounter]);

  function handleLastUpdatedAtChanged() {
    console.log('lastUpdatedAt changed - reloading game data...');
    reloadGameData();
  }

  const demoGameData = {
    period: "1st",
  };

  useEffect(() => {
    if (updatedAtData && updatedAtData.lastUpdatedAt != lastUpdatedAtState?.lastUpdatedAt) {
      console.log(updatedAtData.lastUpdatedAt, '!=', lastUpdatedAtState?.lastUpdatedAt);
      setLastUpdatedAtState(updatedAtData);
      handleLastUpdatedAtChanged();
    }
  }, [updatedAtData]);

  const finalData = useMemo(() => {
    if (!gameData) return null;

    const periods = gameData.Periods
      ?.map(lp => ({
        name: lp.name,
        short: lp.code,
        duration: lp.duration,
        index: lp.priority,
      }));
    periods?.sort((a, b) => a.index - b.index);

    const sourceEvents = gameData.GameEventGoals.map(goal => ({
      ...goal,
      entity: 'GameEventGoal',
    }));

    const sortedEvents = sortGameEvents(sourceEvents, gameData.HomeTeam?.teamId, gameData.AwayTeam?.teamId, periods);

    console.log('sortedEvents', sortedEvents.map(e => ({
      period: e.period,
      gameTime: e.gameTime,
      remainingTime: e.remainingTime,
      scoredBy: e.ScoredByPlayer?.lastName,
      score: `${e.scoreHome}:${e.scoreAway}`,
    })))

    const finalData = {
      ...demoGameData,
      ...gameData,
      GameStreakHomeTeam: gameData.GameStreakHomeTeam.sort((a, b) => b.startDate?.localeCompare(a.startDate)).slice(0, GAME_STREAK_MAX_LENGTH).reverse(),
      GameStreakAwayTeam: gameData.GameStreakAwayTeam.sort((a, b) => b.startDate?.localeCompare(a.startDate)).slice(0, GAME_STREAK_MAX_LENGTH).reverse(),
      scoreHome: gameData.HomeTeamGoals ?? '-',
      scoreAway: gameData.AwayTeamGoals ?? '-',
      Lineup: {
        [gameData.HomeTeam.short]: gameData.Lineups.filter(l => l.teamId === gameData.HomeTeam.teamId),
        [gameData.AwayTeam.short]: gameData.Lineups.filter(l => l.teamId === gameData.AwayTeam.teamId),
      },
      Goals: sortedEvents.map(gameEventGoal => ({
        timeMinute: gameEventGoal.gameTime ? gameEventGoal.gameTime.split(':')[0] : null,
        team:
          gameData.HomeTeam.teamId == gameEventGoal.scoredByTeamId ? gameData.HomeTeam.short :
          gameData.AwayTeam.teamId == gameEventGoal.scoredByTeamId ? gameData.AwayTeam.short :
          '?',
        shotByPlayer: gameEventGoal.ScoredByPlayer ?? {},
        assistByPlayer1: gameEventGoal.AssistedBy1Player ?? {},
        assistByPlayer2: gameEventGoal.AssistedBy2Player ?? {},
        scoreHome: gameEventGoal.scoreHome ?? '-',
        scoreAway: gameEventGoal.scoreAway ?? '-',
      })),
      Statistics: gameData.Statistics.map(s => (
        s.type == 'PIM' ? {
          type: 'PIM',
          homeTeamValue: Math.floor(s.homeTeamValue * 100)/100,
          awayTeamValue: Math.floor(s.awayTeamValue * 100)/100,
        } : s)),
    };

    deepObjectTraversal(finalData, (obj) => {
      if (obj.logoUrl) {
        obj.logo = obj.logoUrl.replace('[size]', 'cropped_md');
      }
    });

    return finalData;
  }, [gameData]);

  if (!finalData) return null;

  return (
    <div className="obs">
      <ObsGame gameData={finalData} />
    </div>
  )
}

const deepObjectTraversal = (obj, callback = () => {}) => {
  if (obj === null || obj === undefined) return;
  
  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      deepObjectTraversal(item, callback);
    });
  } else if (typeof obj === 'object') {
    callback(obj);
    Object.keys(obj).forEach(key => {
      if (typeof obj[key] === 'object' || Array.isArray(obj[key])) {
        deepObjectTraversal(obj[key], callback);
      }
    });
  }
};

export default ObsGameWrapper